import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z as zod } from "zod";
import { fetchApiGet, fetchApiPost, fetchApiPut } from "./utils";
import { DeferredPaymentType } from "./deferred_payments";
import { Customer } from "./reports";
import { transformToDecimal } from "./common";

export interface PaginatedResult<T> {
  data: T[],
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  },
}

/**
 * A page of a list that is paginated without a count query: the server asks for one
 * row more than the page size and reports whether it got it. There is no total.
 */
export interface PagedResult<T> {
  data: T[],
  pagination: {
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
  },
}

/**
 * `Todos.source_type` for a row the call-intake graph created.
 *
 * One constant rather than the string literal at each render site: the value is
 * written by a different repo (`todos.persist` in lhc-mono-bun), so a typo here
 * would not fail a build or a test — the pill would simply never appear.
 */
export const AUTOMATION_SOURCE_TYPE = "call_log_automation";
export const MAX_FLAG_REASON_CHARACTERS = 5000;
export const MAX_HIDDEN_REASON_CHARACTERS = 5000;

/**
 * Whose to-dos a list request asks for.
 *
 * The filter runs on the server — the list is paginated, so narrowing the page
 * that already came back would leave "Page X of Y" and the row counts describing
 * a set the user cannot see. `"both"` is the API's default; the portal opens on
 * `"human"` because automation rows are the noisier half and a person looking at
 * this screen is usually working their own list.
 */
export type TodoSourceFilter = "human" | "both" | "automation";

export const todoSourceFilterOptions: { value: TodoSourceFilter; label: string }[] = [
  { value: "human", label: "Human" },
  { value: "both", label: "Both" },
  { value: "automation", label: "Automation" },
];

export interface Activity {
  id: string;
  resource_type: "NOTE" | "TODO";
  content: string;
  status?: "COMPLETED" | "TODO";
  customer_id: string;
  created_at: string;
  updated_at: string;
  created_by_id: string;
  last_updated_by_id: string;

  last_completed_by_id?: string;
  last_assigned_to_id?: string;
  last_assigned_at?: Date;

  /**
   * Relations the API attaches so a name needs no client-side lookup.
   *
   * These replace resolving `*_by_id` against `useListMinimalUsers`, which is
   * `enabled = true` minus the automation user — so anything written by
   * automation, or by someone since disabled, resolved to "Unknown". The API
   * follows the foreign key with no such filter.
   *
   * All optional and nullable: a NOTE carries only `Customer`, `CreatedBy` and
   * `LastUpdatedBy`, an unassigned or uncompleted TODO has `null` for those two
   * relations, and an API that predates this change sends none of them.
   */
  /**
   * Where the row came from. `"portal"` for anything a person created — the
   * column default, so it is also what every row predating call intake reads.
   * `AUTOMATION_SOURCE_TYPE` marks one the call-intake graph wrote.
   *
   * Optional because NOTE rows have no such column.
   */
  source_type?: string;
  source_entity_id?: string | null;
  flagged_by_user_id?: string | null;
  flagged_reason?: string | null;
  flagged_date?: string | null;
  /** Only on rows from the staged-list endpoint. */
  Call?: StagedTodoCall | null;
  approved_by_user_id?: string | null;
  approved_date?: string | null;
  hidden_by_user_id?: string | null;
  hidden_reason?: string | null;
  hidden_date?: string | null;

  /** `client_id` is absent from an older backend. */
  Customer?: { client_id?: string | null; long_name: string; is_end_user?: boolean } | null;
  CreatedBy?: { name: string | null } | null;
  LastUpdatedBy?: { name: string | null } | null;
  LastCompletedBy?: { name: string | null } | null;
  LastAssignedTo?: { name: string | null } | null;
}

export enum CustomerType {
  Carrier = "carrier",
  Producer = "producer",
  EndUser = "end_user",
  PaymentSplit = "payment_split",
  Client = "client",
}

export const customerTypeOptions = [{
  value: CustomerType.Carrier, label: "Carrier",
}, {
  value: CustomerType.Producer, label: "Producer",
}, {
  value: CustomerType.EndUser, label: "End User",
}, {
  value: CustomerType.PaymentSplit, label: "Payment Split",
}, {
  value: CustomerType.Client, label: "Client",
}];

export const SourcePhoneNumberValidation = zod.object({
  source_type: zod.enum(["destination", "mobile_user"]),
  label: zod.string(),
  phone_number: zod.string().min(1),
});

export const SourceEmailValidation = zod.object({
  source_type: zod.enum(["payment", "payment_ach", "payment_check", "pdf_signature"]),
  label: zod.string(),
  email: zod.string().email(),
});

export const ListCustomerActivities = zod.object({
  customer_type: zod.enum(["risk", "end_user"]).optional().nullable(),
  entity_type: zod.enum(["NOTE", "TODO"]),
  source: zod.enum(["human", "both", "automation"]).optional(),
  status: zod.enum(["COMPLETED", "TODO"]).optional(),
  customer_id: zod.string().uuid(),
  trader_id: zod.string().uuid().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(50).default(15),
  start_date: zod.string().nullable().optional(),
  end_date: zod.string().nullable().optional(),
  assigned_to_id: zod.string().uuid().optional(),
  created_by_id: zod.string().uuid().optional(),
  updated_by_id: zod.string().uuid().optional(),
  completed_by_id: zod.string().uuid().optional(),
});

export const ListCustomersTodos = zod.object({
  customer_type: zod.enum(["risk", "end_user"]).optional().nullable(),
  entity_type: zod.enum(["NOTE", "TODO"]).optional(),
  source: zod.enum(["human", "both", "automation"]).optional(),
  customer_id: zod.string().uuid().optional(),
  trader_id: zod.string().uuid().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(50).default(15),
  status: zod.enum(["COMPLETED", "TODO"]).optional(),
  start_date: zod.string().nullable().optional(),
  end_date: zod.string().nullable().optional(),
  assigned_to_id: zod.string().uuid().optional(),
  created_by_id: zod.string().uuid().optional(),
  updated_by_id: zod.string().uuid().optional(),
  completed_by_id: zod.string().uuid().optional(),
});

export const CreateTodoInput = zod.object({
  activity_type: zod.enum(["risk", "sales_notes"]).optional().nullable(),
  resource_type: zod.enum(["TODO"]),
  customer_id: zod.string().uuid(),
  content: zod.string().min(1),
  assigned_to_id: zod.string().uuid().optional().nullable()
});

export const CreateNoteInput = zod.object({
  activity_type: zod.enum(["risk", "sales_notes"]).optional().nullable(),
  resource_type: zod.enum(["NOTE"]),
  customer_id: zod.string().uuid(),
  content: zod.string().min(1),
});

export const UpdateNoteInput = CreateNoteInput.extend({
  id: zod.string().uuid(),
});

export const UpdateTodoInput = CreateTodoInput.extend({
  id: zod.string().uuid(),
  status: zod.enum(["COMPLETED", "TODO"]),
  assigned_to_id: zod.string().uuid().optional().nullable()
});

export const CreateCustomerActivityInput = CreateNoteInput.or(CreateTodoInput);
export const UpdateCustomerActivityInput = UpdateNoteInput.or(UpdateTodoInput);

export const MarkTodoFlagInput = zod.object({
  todo_id: zod.string().uuid(),
  flagged_reason: zod.string().trim().min(1).max(MAX_FLAG_REASON_CHARACTERS),
});

export const UpdateTodoFlagInput = MarkTodoFlagInput;

export const RemoveTodoFlagInput = zod.object({
  todo_id: zod.string().uuid(),
});

export interface TodoFlagResponse {
  entity: Activity;
}

export interface RemoveTodoFlagResponse extends TodoFlagResponse {
  activity_id: string;
}

export type UpdateTodoFlagResponse = RemoveTodoFlagResponse;

export interface TodoTranscriptResponse {
  transcript: string;
}

/**
 * One side of a call. A Loom (internal phone system) user has `loom_id`, an outside caller has
 * `phone_number`. `user_name` is the staff member the Loom id resolved to, null if it matched nobody.
 */
export interface CallParty {
  phone_number: string | null;
  loom_id: string | null;
  user_name: string | null;
}

export interface StagedTodoCall {
  inbound_id: string;
  call_started_at: string | null;
  call_type: string | null;
  confidence: number | null;
  finished_at: string | null;
  has_transcript: boolean;
  /** Null when the recording has no call record. */
  from: CallParty | null;
  to: CallParty | null;
}

export interface StagedTodo extends Activity {
  Call: StagedTodoCall | null;
  FlaggedBy?: { name: string | null } | null;
}

/** One transcription attempt of a call. `failures` holds the failure action codes recorded against it. */
export interface CallTranscriptionSummary {
  id: string;
  generated_with: string | null;
  created_at: string;
  has_transcript: boolean;
  failures: string[];
}

/** A recorded call from `GET customer/call-transcriptions`, with its transcriptions (rough first) and todos in any review state. */
export interface CallTranscriptionCall {
  id: string;
  created_at: string;
  call_started_at: string | null;
  /** `client_id` is absent from an older backend. */
  customer: { id: string; client_id?: string | null; long_name: string } | null;
  /** Null when the recording has no call record. */
  from: CallParty | null;
  to: CallParty | null;
  transcriptions: CallTranscriptionSummary[];
  todos: StagedTodo[];
}

export const ListCallTranscriptions = zod.object({
  customer_ids: zod.array(zod.string().uuid()).optional(),
  trader_ids: zod.array(zod.string().uuid()).optional(),
  start_date: zod.string().optional(),
  end_date: zod.string().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(100).default(25),
});

export type TranscriptionView = "needs_review" | "visible" | "removed";

export const transcriptionViewOptions: { value: TranscriptionView; label: string }[] = [
  { value: "needs_review", label: "Needs review" },
  { value: "visible", label: "Visible" },
  { value: "removed", label: "Removed" },
];

export const ListStagedTodos = zod.object({
  view: zod.enum(["needs_review", "visible", "removed"]).default("needs_review"),
  customer_ids: zod.array(zod.string().uuid()).optional(),
  trader_ids: zod.array(zod.string().uuid()).optional(),
  call_id: zod.string().uuid().optional(),
  start_date: zod.string().optional(),
  end_date: zod.string().optional(),
  page: zod.coerce.number().min(1).default(1),
  pageSize: zod.coerce.number().min(1).max(100).default(25),
});

export const TodoReviewInput = zod.object({
  todo_id: zod.string().uuid(),
});

export const HideTodoInput = zod.object({
  todo_id: zod.string().uuid(),
  hidden_reason: zod.string().trim().min(1).max(MAX_HIDDEN_REASON_CHARACTERS),
});

export const ChangeTodoCustomerInput = zod.object({
  todo_id: zod.string().uuid(),
  customer_id: zod.string().uuid(),
});

export interface TodoReviewResponse {
  entity: Activity;
  activity_id: string;
}

export const CreateCustomerValidation = zod.object({
  id: zod.string().optional(),

  client_id: zod.string(),
  short_name: zod.string().optional().nullable(),
  long_name: zod.string(),

  is_commercial: zod.boolean(),
  is_cfr: zod.boolean().default(false),

  harvest_iq_profile: zod.string().trim().optional().nullable(),
  skip_harvest_iq_sync: zod.boolean().default(false),

  email: zod.union([zod.string().email(), zod.literal(""), zod.null(), zod.undefined()]).transform(item => {
    if (!item?.trim()) return null;
    return item;
  }),
  address_line_one: zod.string().optional().nullable(),
  address_line_two: zod.string().optional().nullable(),
  city: zod.string().optional().nullable(),
  state: zod.string().optional().nullable(),
  zipcode: zod.string().optional().nullable(),
  country: zod.string().optional().nullable(),
  county: zod.string().optional().nullable(),

  phone_number: zod.string().optional().nullable(),
  source_phone_numbers: zod.array(SourcePhoneNumberValidation).optional(),
  source_emails: zod.array(SourceEmailValidation).optional(),

  preferred_defer_payment_type: zod.enum([DeferredPaymentType.ACH, DeferredPaymentType.CHECK]).optional().nullable(),
  deferred_payment_lien_status: zod.enum(['active']).optional().nullable(),

  gps_latitude: zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_latitude" })),
  gps_longitude: zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_longitude" })),

  marketing_fee: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "marketing_fee" }))),

  customer_type: zod.enum([CustomerType.Carrier, CustomerType.Producer, CustomerType.EndUser, CustomerType.Client, CustomerType.PaymentSplit]),
  active_account_codes: zod.array(zod.string()).default([]),

  trader_id: zod.string().uuid().optional().nullable(),

  partner_names: zod.array(zod.string()).optional().nullable(),
  shirt_sizes: zod.array(zod.string()).optional().nullable(),
  original_acres: zod.number().optional().nullable(),
  bol_sequence: zod.string().optional().nullable(),
  notes: zod.string().optional().nullable(),

  shipping_instructions: zod.string().optional().nullable(),
  regular_hours: zod.string().optional().nullable(),
  current_week_hours: zod.string().optional().nullable(),
  next_week_hours: zod.string().optional().nullable(),

  signed_up_date: zod.string().optional().nullable(),

  onboarding_instance_id: zod.string().uuid().optional().nullable(),
});

function nullableUpdatableField(item: any) {
  if (item == null) { return item; }
  switch (typeof item) {
    case "string":
      if (!item?.trim()) return null;
      return item;
    default:
      return item;
  }
}

export const PartialUpdateCustomerValidation = zod.object({
  short_name: zod.string().optional().nullable(),
  long_name: zod.string().optional().nullable(),

  is_commercial: zod.boolean().optional().nullable(),
  is_cfr: zod.boolean().optional().nullable(),

  harvest_iq_profile: zod.preprocess(nullableUpdatableField, zod.string().trim().optional().nullable()),
  skip_harvest_iq_sync: zod.boolean().optional().nullable(),

  email: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  phone_number: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),

  source_phone_numbers: zod.array(SourcePhoneNumberValidation).optional().nullable(),
  source_emails: zod.array(SourceEmailValidation).optional().nullable(),

  address_line_one: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  address_line_two: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  city: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  zipcode: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  state: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  country: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  county: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),

  gps_latitude: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_latitude" }))),
  gps_longitude: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "gps_longitude" }))),

  marketing_fee: zod.preprocess(nullableUpdatableField, zod.union([zod.null(), zod.undefined(), zod.string()]).transform(transformToDecimal({ key: "marketing_fee" }))),

  preferred_defer_payment_type: zod.preprocess(nullableUpdatableField, zod.enum([DeferredPaymentType.ACH, DeferredPaymentType.CHECK]).optional().nullable()),
  deferred_payment_lien_status: zod.preprocess(nullableUpdatableField, zod.enum(["active"]).optional().nullable()),

  customer_type: zod.preprocess(nullableUpdatableField, zod.enum([CustomerType.Carrier, CustomerType.Producer, CustomerType.EndUser, CustomerType.Client, CustomerType.PaymentSplit]).optional().nullable()).optional().nullable(),
  active_account_codes: zod.array(zod.string()).optional().nullable(),

  signed_up_date: zod.string().optional().nullable(),

  trader_id: zod.preprocess(nullableUpdatableField, zod.string().uuid().optional().nullable()),

  notes: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),

  shipping_instructions: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  regular_hours: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  current_week_hours: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
  next_week_hours: zod.preprocess(nullableUpdatableField, zod.string().optional().nullable()),
});

export type BaseCustomer = typeof CreateCustomerValidation._type;

export interface ListCustomerOptions {
  minimal?: boolean;
  hasRiskProfile?: boolean;
  enabled?: boolean;
  fetchInBackground?: boolean;
  customer_type?: "end_user" | null;
}

export const FileUploadValidation = zod.object({
  filename: zod.string(),
  mimetype: zod.string(),
});

export const BulkUploadValidation = zod.object({
  zip_file: FileUploadValidation
});

export function useListClientBrokerAccountNumbers() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["baseCustomersAccountNumbers"],
    queryFn: () => fetchApiGet<{
      unassignedAccountNumberList: string[],
      assignedAccountNumberList: string[],
    }>("customer/broker/account_numbers", queryParams),
    retry: 1,
  });
}

/** Prefix of every customer list query: invalidate it after anything that writes a customer. */
export const baseCustomersQueryKey = ["baseCustomers"] as const;

export function useListCustomers(options?: ListCustomerOptions) {
  const queryParams = new URLSearchParams();
  if (options?.minimal) {
    queryParams.set("minimal", "true");
  }
  if (typeof options?.hasRiskProfile === "boolean") {
    queryParams.set("hasRiskProfile", options.hasRiskProfile === true ? "true" : "false");
  }
  if (options?.customer_type) {
    queryParams.set("customer_type", options.customer_type);
  }

  return useQuery({
    queryKey: [...baseCustomersQueryKey, queryParams.toString()],
    queryFn: () => fetchApiGet<Customer[]>("customer", queryParams),
    retry: 1,
    enabled: options?.enabled ?? true,
    keepPreviousData: options?.fetchInBackground ?? false,
  });
}

export interface AvailableOnboardingInstanceInfo {
  id: string;
  created_at: string;
  template_id: string;
  trigger_meta_data: any;
  template: {
    name: string;
  }
}

export const customerOnboardingInstancesQueryKey = ["customerOnboardingInstances"] as const;

export function useListOnboardingCustomerInstances() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: customerOnboardingInstancesQueryKey,
    queryFn: () => fetchApiGet<AvailableOnboardingInstanceInfo[]>("manage/customer/template/instances/onboarding/available", queryParams),
    retry: 1,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (data: BaseCustomer) => {
    const result = await fetchApiPost<BaseCustomer>(
      "customer",
      data,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
      queryClient.invalidateQueries({ queryKey: customerOnboardingInstancesQueryKey });
    }
    return result;
  };
}

export function useUpdatePartialCustomer() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (id: string, data: Partial<BaseCustomer>) => {

    const result = await fetchApiPut<BaseCustomer>(
      `manage/customer/${id}`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["baseCustomers"] });
    }

    return result;
  };
}


export function useUploadBulkPaymentRemittanceJob() {
  const queryParams = new URLSearchParams();

  return async (data: typeof BulkUploadValidation._type) => {
    const result = await fetchApiPost<{ job_id: string; document_id: string; link: string }>(
      `customer/payment/pdf/bulk`,
      data,
      queryParams,
    );

    return result;
  };
}

export function useListCustomerActivities(options: zod.infer<typeof ListCustomerActivities>) {
  const queryParams = assembleCustomerActivities(options);

  return useQuery({
    queryKey: ["customerActivities", queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResult<any>>(`customer/${options.customer_id}/summary`, queryParams),
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

function assembleCustomerActivities(options: zod.infer<typeof ListCustomerActivities>) {
  const queryParams = new URLSearchParams();
  if (options.customer_type) { queryParams.set("customer_type", options.customer_type); }
  if (options.entity_type) { queryParams.set("entity_type", options.entity_type); }
  if (options.source) { queryParams.set("source", options.source); }
  if (options.page) { queryParams.set("page", options.page.toString()); }
  if (options.pageSize) { queryParams.set("pageSize", options.pageSize.toString()); }
  if (options.customer_id) { queryParams.set("customer_id", options.customer_id); }
  if (options.trader_id) { queryParams.set("trader_id", options.trader_id); }
  if (options.status) { queryParams.set("status", options.status); }

  if (options.start_date) { queryParams.set("start_date", options.start_date); }
  if (options.end_date) { queryParams.set("end_date", options.end_date); }
  if (options.assigned_to_id) { queryParams.set("assigned_to_id", options.assigned_to_id); }
  if (options.created_by_id) { queryParams.set("created_by_id", options.created_by_id); }
  if (options.updated_by_id) { queryParams.set("updated_by_id", options.updated_by_id); }
  if (options.completed_by_id) { queryParams.set("completed_by_id", options.completed_by_id); }

  return queryParams;
}

export function useListCustomersTodos(options: zod.infer<typeof ListCustomersTodos>) {
  const queryParams = assembleCustomerActivities({ ...options, entity_type: options.entity_type || "TODO" } as any);

  return useQuery({
    queryKey: ["customersActivities", queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResult<any>>(`customer/todos`, queryParams),
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

export function useCreateActivity() {
  const queryParams = new URLSearchParams();
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof CreateCustomerActivityInput>) => {
    const result = await fetchApiPost<{}>(
      `customer/${data.customer_id}/activity`,
      data,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["customerActivities"] });
      queryClient.invalidateQueries({ queryKey: ["customersActivities"] });
    }

    return result;
  };
}

export function useUpdateActivity() {
  const queryParams = new URLSearchParams();
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof UpdateCustomerActivityInput>) => {
    const result = await fetchApiPut<{}>(
      `customer/${data.customer_id}/activity`,
      data,
      queryParams,
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

export function useMarkTodoFlag() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof MarkTodoFlagInput>) => {
    const result = await fetchApiPost<TodoFlagResponse>(
      `customer/todos/${data.todo_id}/flag`,
      { flagged_reason: data.flagged_reason },
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

export function useUpdateTodoFlag() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof UpdateTodoFlagInput>) => {
    const result = await fetchApiPost<UpdateTodoFlagResponse>(
      `customer/todos/${data.todo_id}/update-flag`,
      { flagged_reason: data.flagged_reason },
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

export function useRemoveTodoFlag() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof RemoveTodoFlagInput>) => {
    const result = await fetchApiPost<RemoveTodoFlagResponse>(
      `customer/todos/${data.todo_id}/remove-flag`,
      {},
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

function invalidateTodoQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["customerActivities"] });
  queryClient.invalidateQueries({ queryKey: ["customersActivities"] });
  queryClient.invalidateQueries({ queryKey: ["stagedTodos"] });
  queryClient.invalidateQueries({ queryKey: ["callTranscriptions"] });
}

export function useListStagedTodos(options: zod.infer<typeof ListStagedTodos>, enabled: boolean = true) {
  const queryParams = new URLSearchParams();
  queryParams.set("view", options.view);
  queryParams.set("page", options.page.toString());
  queryParams.set("pageSize", options.pageSize.toString());
  if (options.customer_ids?.length) { queryParams.set("customer_ids", options.customer_ids.join(",")); }
  if (options.trader_ids?.length) { queryParams.set("trader_ids", options.trader_ids.join(",")); }
  if (options.call_id) { queryParams.set("call_id", options.call_id); }
  if (options.start_date) { queryParams.set("start_date", options.start_date); }
  if (options.end_date) { queryParams.set("end_date", options.end_date); }

  return useQuery({
    queryKey: ["stagedTodos", queryParams.toString()],
    queryFn: () => fetchApiGet<PagedResult<StagedTodo>>("customer/todos/staged", queryParams),
    enabled,
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

export function useListCallTranscriptions(options: zod.infer<typeof ListCallTranscriptions>) {
  const queryParams = new URLSearchParams();
  queryParams.set("page", options.page.toString());
  queryParams.set("pageSize", options.pageSize.toString());
  if (options.customer_ids?.length) { queryParams.set("customer_ids", options.customer_ids.join(",")); }
  if (options.trader_ids?.length) { queryParams.set("trader_ids", options.trader_ids.join(",")); }
  if (options.start_date) { queryParams.set("start_date", options.start_date); }
  if (options.end_date) { queryParams.set("end_date", options.end_date); }

  return useQuery({
    queryKey: ["callTranscriptions", queryParams.toString()],
    queryFn: () => fetchApiGet<PagedResult<CallTranscriptionCall>>("customer/call-transcriptions", queryParams),
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

export function useGetCallTranscriptionTranscript(transcriptionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["callTranscriptionTranscript", transcriptionId],
    queryFn: () => fetchApiGet<TodoTranscriptResponse>(`customer/call-transcriptions/${transcriptionId}/transcript`),
    enabled: enabled && !!transcriptionId,
    retry: 1,
    cacheTime: 0,
  });
}

export function useApproveTodo() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof TodoReviewInput>) => {
    const result = await fetchApiPost<TodoReviewResponse>(`customer/todos/${data.todo_id}/approve`, {});

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

export function useHideTodo() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof HideTodoInput>) => {
    const result = await fetchApiPost<TodoReviewResponse>(
      `customer/todos/${data.todo_id}/hide`,
      { hidden_reason: data.hidden_reason },
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

/** Staged automation todos only; the new customer must have a risk profile. */
export function useChangeTodoCustomer() {
  const queryClient = useQueryClient();

  return async (data: zod.infer<typeof ChangeTodoCustomerInput>) => {
    const result = await fetchApiPost<TodoReviewResponse>(
      `customer/todos/${data.todo_id}/customer`,
      { customer_id: data.customer_id },
    );

    if (result.success) {
      invalidateTodoQueries(queryClient);
    }

    return result;
  };
}

export function useGetTodoTranscript(todoId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["todoTranscript", todoId],
    queryFn: () => fetchApiGet<TodoTranscriptResponse>(`customer/todos/${todoId}/transcript`),
    enabled: enabled && !!todoId,
    retry: 1,
    cacheTime: 0,
  });
}
